import React, { useEffect, useState } from 'react';
import { CheckCircle, CheckCircle2, Loader, XCircle, AlertTriangle, Upload, Eye, Download, Trash2 } from 'lucide-react';
import { apiClient } from '../../config/api';
import Modal from '../../components/Modal';

const AdminMessageSuitePage = () => {
    const [groups, setGroups] = useState([]);
    const [mappingsByGroup, setMappingsByGroup] = useState(new Map());
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [status, setStatus] = useState(null); // 'success' | 'error' | null
    const [statusMessage, setStatusMessage] = useState('');
    const [showGenerateWarning, setShowGenerateWarning] = useState(false);
    const [messageEstimate, setMessageEstimate] = useState(null);
    const [loadingEstimate, setLoadingEstimate] = useState(false);
    const [suiteGenerated, setSuiteGenerated] = useState(false);
    const [generatedSuiteData, setGeneratedSuiteData] = useState(null);
    const [pushingToVoiceBox, setPushingToVoiceBox] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [showPushWarning, setShowPushWarning] = useState(false);
    const [csvExport, setCsvExport] = useState(null);
    const [importedMessages, setImportedMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [deletingMessages, setDeletingMessages] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingMessageId, setDeletingMessageId] = useState(null);
    const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
    const [deletingAll, setDeletingAll] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const [groupsResult, mappingsResult] = await Promise.all([
                    apiClient.get('/groups'),
                    apiClient.get('/voicebox/client-mappings')
                ]);

                const sortedGroups = (groupsResult || []).sort((a, b) => a.name.localeCompare(b.name));
                setGroups(sortedGroups);

                const mappings = new Map();
                (mappingsResult.mappings || []).forEach(mapping => {
                    mappings.set(String(mapping.group_id), mapping);
                });
                setMappingsByGroup(mappings);
            } catch (error) {
                console.error('Failed to load message suite data:', error);
                setStatus('error');
                setStatusMessage('Failed to load groups or VoiceBox mappings.');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    const loadEstimate = async () => {
        if (!selectedGroupId) return;
        
        setLoadingEstimate(true);
        try {
            const estimate = await apiClient.get(`/voicebox/groups/${selectedGroupId}/message-suite/estimate`);
            setMessageEstimate(estimate);
        } catch (error) {
            console.error('Failed to load estimate:', error);
            setMessageEstimate(null);
        } finally {
            setLoadingEstimate(false);
        }
    };

    useEffect(() => {
        if (selectedGroupId) {
            loadEstimate();
            loadImportedMessages();
        } else {
            setMessageEstimate(null);
            setImportedMessages([]);
        }
    }, [selectedGroupId]);

    const loadImportedMessages = async () => {
        if (!selectedGroupId) return;
        
        setLoadingMessages(true);
        try {
            const result = await apiClient.get(`/voicebox/groups/${selectedGroupId}/messages`);
            setImportedMessages(result.messages || []);
        } catch (error) {
            console.error('Failed to load imported messages:', error);
            setImportedMessages([]);
        } finally {
            setLoadingMessages(false);
        }
    };

    const handleDeleteMessages = async () => {
        if (!selectedGroupId) return;
        
        setDeletingMessages(true);
        try {
            const result = await apiClient.delete(`/voicebox/groups/${selectedGroupId}/messages`);
            setStatusMessage(result.message || `Successfully deleted ${result.deleted} message(s)`);
            setStatus('success');
            setShowDeleteConfirm(false);
            await loadImportedMessages();
        } catch (error) {
            console.error('Failed to delete messages:', error);
            setStatus('error');
            setStatusMessage(error.response?.data?.error || error.message || 'Failed to delete messages.');
        } finally {
            setDeletingMessages(false);
        }
    };

    const handleDeleteMessage = async (messageId, messageName) => {
        setDeletingMessageId(messageId);
        try {
            const result = await apiClient.delete(`/voicebox/message-catalog/${messageId}`);
            setStatusMessage(result.message || `Successfully deleted message "${messageName}"`);
            setStatus('success');
            await loadImportedMessages();
        } catch (error) {
            console.error('Failed to delete message:', error);
            setStatus('error');
            setStatusMessage(error.response?.data?.error || error.message || 'Failed to delete message.');
        } finally {
            setDeletingMessageId(null);
        }
    };

    const handleDeleteAllMessages = async () => {
        setDeletingAll(true);
        try {
            const result = await apiClient.delete('/voicebox/message-catalog', {
                confirm_delete_all: true
            });
            setStatusMessage(result.message || `Successfully deleted all ${result.deleted} message(s)`);
            setStatus('success');
            setShowDeleteAllConfirm(false);
            await loadImportedMessages();
        } catch (error) {
            console.error('Failed to delete all messages:', error);
            setStatus('error');
            setStatusMessage(error.response?.data?.error || error.message || 'Failed to delete all messages.');
        } finally {
            setDeletingAll(false);
        }
    };

    const handleGenerateClick = () => {
        if (!selectedGroupId) {
            setStatus('error');
            setStatusMessage('Select a dealership group first.');
            return;
        }
        setShowGenerateWarning(true);
    };

    const handleGenerateConfirm = async () => {
        setShowGenerateWarning(false);
        setGenerating(true);
        setStatus(null);
        setStatusMessage('');
        setSuiteGenerated(false);
        setGeneratedSuiteData(null);

        try {
            const suiteResult = await apiClient.post(`/voicebox/groups/${selectedGroupId}/message-suite`, {
                folder_prefix: 'Onboarding',
                overwrite: false
            });

            const mappingId = suiteResult.mapping_id;
            if (mappingId) {
                await apiClient.get(`/voicebox/client-mappings/${mappingId}/messages`, {}, 600000);
                // Reload imported messages for the group after syncing
                await loadImportedMessages();
            }

            const brandInfo = (suiteResult.brand_names && suiteResult.brand_names.length > 0)
                ? ` Brands applied: ${suiteResult.brand_names.join(', ')}.`
                : '';
            setStatus('success');
            setStatusMessage(`Message suite generated and synced successfully.${brandInfo}`);
            setSuiteGenerated(true);
            setGeneratedSuiteData(suiteResult);
            // Reload estimate after generation
            loadEstimate();
        } catch (error) {
            console.error('Failed to generate message suite:', error);
            setStatus('error');
            setStatusMessage(error.message || 'Failed to generate message suite.');
        } finally {
            setGenerating(false);
        }
    };

    const handlePushToVoiceBox = async () => {
        if (!generatedSuiteData || !generatedSuiteData.mapping_id || !previewData) {
            setStatus('error');
            setStatusMessage('No preview data available to push.');
            return;
        }

        setShowPushWarning(true);
    };

    const handlePushConfirm = async () => {
        setShowPushWarning(false);
        setPushingToVoiceBox(true);
        setStatus(null);
        setStatusMessage('');

        try {
            // Push to VoiceBox with explicit confirmation
            const pushResult = await apiClient.post(`/voicebox/groups/${selectedGroupId}/message-suite/push`, {
                folder_prefix: 'Onboarding',
                overwrite: false,
                confirm_push: true
            });

            // Sync the catalog after push
            if (generatedSuiteData.mapping_id) {
                await apiClient.get(`/voicebox/client-mappings/${generatedSuiteData.mapping_id}/messages`, {}, 600000);
                // Reload imported messages for the group
                await loadImportedMessages();
            }

            const brandInfo = (pushResult.brand_names && pushResult.brand_names.length > 0)
                ? ` Brands applied: ${pushResult.brand_names.join(', ')}.`
                : '';
            
            setStatus('success');
            setStatusMessage(`Message suite successfully pushed to VoiceBox database.${brandInfo} ${pushResult.warning || ''}`);
            setSuiteGenerated(true);
            setShowPreview(false);
            
            // Reload estimate after push
            loadEstimate();
        } catch (error) {
            console.error('Failed to push to VoiceBox:', error);
            setStatus('error');
            setStatusMessage(error.message || 'Failed to push message suite to VoiceBox.');
        } finally {
            setPushingToVoiceBox(false);
        }
    };

    const selectedMapping = mappingsByGroup.get(String(selectedGroupId));

    return (
        <div className="max-w-3xl mx-auto space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-900">Message Suites</h1>
                <p className="text-gray-600 mt-2">
                    Generate VoiceBox message folders and templates for a selected dealership.
                </p>
                <p className="text-sm text-gray-500 mt-2">
                    Contact Plans are now managed as Workflows. Use the import tools below to bring VoiceBox contact plans into Onboarding drafts.
                </p>
                <p className="text-sm text-gray-500 mt-1">
                    Brand-specific templates are grouped into brand folders when your locations have brands assigned.
                </p>
            </div>

            {/* Message Management Section - Always Visible When Group Selected */}
            {selectedGroupId && (
                <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                <Trash2 className="text-red-600" size={20} />
                                Message Management
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Delete imported messages from the catalog. This only removes them from Onboarding, not from VoiceBox.
                            </p>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">Individual Messages</span>
                                <span className="text-xs text-gray-500">{importedMessages.length} message{importedMessages.length !== 1 ? 's' : ''}</span>
                            </div>
                            <p className="text-xs text-gray-600 mb-3">
                                Delete messages one at a time using the delete button in each row below.
                            </p>
                            {importedMessages.length > 0 ? (
                                <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">
                                    ✓ {importedMessages.length} message{importedMessages.length !== 1 ? 's' : ''} available to delete
                                </div>
                            ) : (
                                <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded p-2">
                                    No messages imported for this group
                                </div>
                            )}
                        </div>

                        <div className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">Delete Group Messages</span>
                                <span className="text-xs text-gray-500">Group Only</span>
                            </div>
                            <p className="text-xs text-gray-600 mb-3">
                                Delete all messages imported for the selected dealership group.
                            </p>
                            <button
                                onClick={() => setShowDeleteConfirm(true)}
                                disabled={deletingMessages || importedMessages.length === 0}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                                title={importedMessages.length === 0 ? 'No messages to delete for this group' : `Delete all ${importedMessages.length} message(s) for this group`}
                            >
                                {deletingMessages ? (
                                    <>
                                        <Loader className="animate-spin" size={16} />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={16} />
                                        Delete All for Group
                                        {importedMessages.length > 0 && ` (${importedMessages.length})`}
                                    </>
                                )}
                            </button>
                        </div>

                        <div className="border-2 border-red-300 rounded-lg p-4 bg-red-50">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-semibold text-red-900">Delete All Messages</span>
                                <span className="text-xs text-red-700 font-medium">⚠️ All Groups</span>
                            </div>
                            <p className="text-xs text-red-800 mb-3">
                                <strong>Warning:</strong> This will delete ALL messages from the catalog, regardless of group.
                            </p>
                            <button
                                onClick={() => setShowDeleteAllConfirm(true)}
                                disabled={deletingAll}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-800 text-white rounded-md hover:bg-red-900 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                                title="Delete all messages from catalog (all groups)"
                            >
                                {deletingAll ? (
                                    <>
                                        <Loader className="animate-spin" size={16} />
                                        Deleting All...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={16} />
                                        Delete All Messages
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white border rounded-lg p-6 space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dealership Group
                    </label>
                    <div className="relative">
                        <select
                            value={selectedGroupId}
                            onChange={(e) => setSelectedGroupId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                            disabled={loading}
                        >
                            <option value="">Select a group...</option>
                            {groups.map(group => {
                                const isSynced = group.is_voicebox_synced === true;
                                return (
                                    <option key={group.id} value={group.id}>
                                        {group.name}{!isSynced && ' (Not Synced)'}
                                    </option>
                                );
                            })}
                        </select>
                        {selectedGroupId && (() => {
                            const selectedGroup = groups.find(g => g.id === selectedGroupId);
                            const isSynced = selectedGroup?.is_voicebox_synced === true;
                            return isSynced ? (
                                <div className="absolute right-10 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                    <CheckCircle2 size={18} className="text-green-600" />
                                </div>
                            ) : null;
                        })()}
                    </div>
                </div>

                {selectedGroupId && !selectedMapping && (
                    <div className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                        No VoiceBox mapping found for this group. Configure the mapping on the VoiceBox Sync page.
                    </div>
                )}

                {selectedMapping && (
                    <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
                        <div>Mapped VoiceBox client: {selectedMapping.voicebox_client_name}</div>
                        <div>Database: {selectedMapping.voicebox_database_name}</div>
                    </div>
                )}

                {/* Estimate Display */}
                {messageEstimate && messageEstimate.estimatedMessages > 0 && (
                    <div className={`p-3 rounded-md border ${
                        messageEstimate.estimatedMessages > 100 
                            ? 'bg-amber-50 border-amber-200 text-amber-800'
                            : 'bg-blue-50 border-blue-200 text-blue-800'
                    }`}>
                        <div className="flex items-start gap-2">
                            <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
                            <div className="text-sm">
                                <p className="font-medium">
                                    Estimated: {messageEstimate.estimatedMessages.toLocaleString()} messages will be created
                                </p>
                                <p className="text-xs mt-1">
                                    {messageEstimate.templateCount} template(s) × {messageEstimate.locationCount} location(s)
                                    {messageEstimate.brandCount > 0 && ` × ${messageEstimate.brandCount} brand(s)`}
                                </p>
                                {messageEstimate.note && (
                                    <p className="text-xs mt-1 font-medium">{messageEstimate.note}</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <button
                    onClick={handleGenerateClick}
                    disabled={loading || generating || !selectedMapping || loadingEstimate}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300"
                >
                    {loadingEstimate ? (
                        <>
                            <Loader className="animate-spin" size={16} />
                            Calculating estimate...
                        </>
                    ) : generating ? (
                        <>
                            <Loader className="animate-spin" size={16} />
                            Generating Message Suite...
                        </>
                    ) : (
                        'Generate Message Suite'
                    )}
                </button>

                {/* Preview Review Section */}
                {showPreview && previewData && (
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="flex items-start gap-3 mb-4">
                            <Eye className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                            <div className="flex-1">
                                <h3 className="font-semibold text-blue-900 mb-2">Preview Generated - Review Before Pushing</h3>
                                <p className="text-sm text-blue-800 mb-3">
                                    The following changes will be made to VoiceBox when you push. No changes have been made yet.
                                </p>
                                
                                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                                    <div>
                                        <span className="font-medium">Folders to Create:</span>
                                        <span className="ml-2 text-blue-900">{previewData.foldersToCreate?.length || 0}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium">Folders to Use:</span>
                                        <span className="ml-2 text-blue-900">{previewData.foldersToUse?.length || 0}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium">Messages to Create:</span>
                                        <span className="ml-2 text-green-700">{previewData.messagesToCreate?.length || 0}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium">Messages to Update:</span>
                                        <span className="ml-2 text-amber-700">{previewData.messagesToUpdate?.length || 0}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium">Messages to Skip:</span>
                                        <span className="ml-2 text-gray-600">{previewData.messagesToSkip?.length || 0}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium">Total Operations:</span>
                                        <span className="ml-2 font-bold text-blue-900">{previewData.totalOperations || 0}</span>
                                    </div>
                                </div>

                                {/* Detailed Preview Table */}
                                {(previewData.messagesToCreate?.length > 0 || previewData.messagesToUpdate?.length > 0) && (
                                    <div className="mt-4 max-h-96 overflow-y-auto border border-blue-200 rounded-md">
                                        <table className="w-full text-xs">
                                            <thead className="bg-blue-100 sticky top-0">
                                                <tr>
                                                    <th className="px-2 py-1 text-left">Operation</th>
                                                    <th className="px-2 py-1 text-left">Folder</th>
                                                    <th className="px-2 py-1 text-left">Message Name</th>
                                                    <th className="px-2 py-1 text-left">Subject</th>
                                                    <th className="px-2 py-1 text-center">Unlayer</th>
                                                    <th className="px-2 py-1 text-center">HTML</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-blue-100">
                                                {previewData.messagesToCreate?.slice(0, 50).map((msg, idx) => (
                                                    <tr key={`create-${idx}`} className="bg-green-50">
                                                        <td className="px-2 py-1 text-green-700 font-medium">CREATE</td>
                                                        <td className="px-2 py-1">{msg.folderName}</td>
                                                        <td className="px-2 py-1 font-medium">{msg.messageName}</td>
                                                        <td className="px-2 py-1">{msg.subject || '-'}</td>
                                                        <td className="px-2 py-1 text-center">{msg.hasUnlayerJson ? '✓' : '-'}</td>
                                                        <td className="px-2 py-1 text-center">{msg.hasHtmlContent ? '✓' : '-'}</td>
                                                    </tr>
                                                ))}
                                                {previewData.messagesToUpdate?.slice(0, 50).map((msg, idx) => (
                                                    <tr key={`update-${idx}`} className="bg-amber-50">
                                                        <td className="px-2 py-1 text-amber-700 font-medium">UPDATE</td>
                                                        <td className="px-2 py-1">{msg.folderName}</td>
                                                        <td className="px-2 py-1 font-medium">{msg.messageName}</td>
                                                        <td className="px-2 py-1">{msg.subject || '-'}</td>
                                                        <td className="px-2 py-1 text-center">{msg.hasUnlayerJson ? '✓' : '-'}</td>
                                                        <td className="px-2 py-1 text-center">{msg.hasHtmlContent ? '✓' : '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {(previewData.messagesToCreate?.length > 50 || previewData.messagesToUpdate?.length > 50) && (
                                            <div className="p-2 bg-blue-50 text-xs text-blue-700 text-center">
                                                Showing first 50 messages. Download CSV for full list.
                                            </div>
                                        )}
                                    </div>
                                )}

                                {csvExport && (
                                    <div className="mt-4">
                                        <button
                                            onClick={() => {
                                                const blob = new Blob([csvExport], { type: 'text/csv' });
                                                const url = URL.createObjectURL(blob);
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = `message-suite-preview-${selectedGroupId}-${Date.now()}.csv`;
                                                document.body.appendChild(a);
                                                a.click();
                                                document.body.removeChild(a);
                                                URL.revokeObjectURL(url);
                                            }}
                                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                                        >
                                            <Download size={16} />
                                            Download CSV Preview (Dev Mode)
                                        </button>
                                        <p className="text-xs text-blue-700 mt-1">
                                            CSV export available in development mode for review
                                        </p>
                                    </div>
                                )}

                                <button
                                    onClick={handlePushToVoiceBox}
                                    disabled={pushingToVoiceBox}
                                    className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 font-semibold"
                                >
                                    {pushingToVoiceBox ? (
                                        <>
                                            <Loader className="animate-spin" size={16} />
                                            Pushing to VoiceBox...
                                        </>
                                    ) : (
                                        <>
                                            <Upload size={16} />
                                            Push to VoiceBox Database (WARNING: This will update VoiceBox)
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {status === 'success' && (
                    <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md p-3 flex items-center gap-2">
                        <CheckCircle size={16} />
                        {statusMessage}
                    </div>
                )}

                {status === 'error' && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3 flex items-center gap-2">
                        <XCircle size={16} />
                        {statusMessage}
                    </div>
                )}
            </div>


            {/* Imported Messages Management */}
            {selectedGroupId && (
                <div className="bg-white border rounded-lg p-6 space-y-4">
                    <div className="flex items-center justify-between border-b pb-4">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">Imported Messages</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Messages imported from VoiceBox for this dealership group. Only messages for the selected group are shown.
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowDeleteConfirm(true)}
                                disabled={deletingMessages || importedMessages.length === 0}
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                title={importedMessages.length === 0 ? 'No messages to delete for this group' : 'Delete all messages for this group'}
                            >
                                {deletingMessages ? (
                                    <>
                                        <Loader className="animate-spin" size={16} />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={16} />
                                        Delete All for Group {importedMessages.length > 0 && `(${importedMessages.length})`}
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => setShowDeleteAllConfirm(true)}
                                disabled={deletingAll}
                                className="flex items-center gap-2 px-4 py-2 bg-red-800 text-white rounded-md hover:bg-red-900 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                title="Delete all messages from catalog (all groups)"
                            >
                                {deletingAll ? (
                                    <>
                                        <Loader className="animate-spin" size={16} />
                                        Deleting All...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={16} />
                                        Delete All Messages
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {loadingMessages ? (
                        <div className="text-center py-8">
                            <Loader className="animate-spin mx-auto" size={32} />
                            <p className="text-gray-600 mt-4">Loading imported messages...</p>
                        </div>
                    ) : importedMessages.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <p>No messages have been imported for this group yet.</p>
                            <p className="text-sm mt-2">Sync messages from VoiceBox to import them.</p>
                            <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                                <p className="text-sm text-gray-600 mb-2">
                                    <strong>Delete Options:</strong>
                                </p>
                                <div className="flex gap-2 justify-center">
                                    <button
                                        onClick={() => setShowDeleteAllConfirm(true)}
                                        disabled={deletingAll}
                                        className="flex items-center gap-2 px-4 py-2 bg-red-800 text-white rounded-md hover:bg-red-900 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                                        title="Delete all messages from catalog (all groups)"
                                    >
                                        {deletingAll ? (
                                            <>
                                                <Loader className="animate-spin" size={14} />
                                                Deleting All...
                                            </>
                                        ) : (
                                            <>
                                                <Trash2 size={14} />
                                                Delete All Messages (All Groups)
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message Name</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">VoiceBox ID</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tags</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Synced</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {importedMessages.map((msg) => (
                                        <tr key={msg.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{msg.message_name}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{msg.subject || '-'}</td>
                                            <td className="px-4 py-3 text-sm font-mono text-gray-700">#{msg.voicebox_message_id}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">
                                                {msg.available_tags && msg.available_tags.length > 0 ? (
                                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                                        {msg.available_tags.length} tag{msg.available_tags.length !== 1 ? 's' : ''}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400">No tags</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-500">
                                                {msg.last_synced_at ? new Date(msg.last_synced_at).toLocaleDateString() : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <button
                                                    onClick={() => {
                                                        if (window.confirm(`Are you sure you want to delete "${msg.message_name}"? This will permanently remove it from the catalog.`)) {
                                                            handleDeleteMessage(msg.id, msg.message_name);
                                                        }
                                                    }}
                                                    disabled={deletingMessageId === msg.id}
                                                    className="px-3 py-1.5 text-red-600 hover:text-white hover:bg-red-600 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-transparent flex items-center gap-1.5 rounded-md border border-red-300 hover:border-red-600 transition-colors"
                                                    title="Delete this message"
                                                >
                                                    {deletingMessageId === msg.id ? (
                                                        <>
                                                            <Loader className="animate-spin" size={16} />
                                                            <span className="text-xs">Deleting...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Trash2 size={16} />
                                                            <span className="text-xs font-medium">Delete</span>
                                                        </>
                                                    )}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Delete Messages Confirmation Modal */}
            <Modal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                title="Delete Imported Messages"
                size="md"
            >
                <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-md">
                        <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={24} />
                        <div>
                            <p className="font-semibold text-red-900 mb-2">
                                Are you sure you want to delete all imported messages for this group?
                            </p>
                            <p className="text-sm text-red-800 mb-2">
                                This will permanently remove {importedMessages.length} message(s) from the catalog.
                            </p>
                            <p className="text-sm text-red-800">
                                <strong>Note:</strong> This only removes messages from the Onboarding catalog. It does not delete messages from VoiceBox.
                            </p>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                        <button
                            onClick={() => setShowDeleteConfirm(false)}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDeleteMessages}
                            disabled={deletingMessages}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300"
                        >
                            {deletingMessages ? 'Deleting...' : 'Yes, Delete All Messages'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Delete All Messages Confirmation Modal */}
            <Modal
                isOpen={showDeleteAllConfirm}
                onClose={() => setShowDeleteAllConfirm(false)}
                title="Delete All Imported Messages"
                size="md"
            >
                <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-red-50 border-2 border-red-300 rounded-md">
                        <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={24} />
                        <div>
                            <p className="font-semibold text-red-900 mb-2">
                                ⚠️ WARNING: Delete ALL Messages from Catalog
                            </p>
                            <p className="text-sm text-red-800 mb-2">
                                This will permanently remove <strong>ALL</strong> imported messages from the catalog, regardless of which group they belong to.
                            </p>
                            <p className="text-sm text-red-800 mb-2">
                                This action cannot be undone.
                            </p>
                            <p className="text-sm text-red-800">
                                <strong>Note:</strong> This only removes messages from the Onboarding catalog. It does not delete messages from VoiceBox.
                            </p>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                        <button
                            onClick={() => setShowDeleteAllConfirm(false)}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                            disabled={deletingAll}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDeleteAllMessages}
                            disabled={deletingAll}
                            className="px-4 py-2 bg-red-800 text-white rounded-md hover:bg-red-900 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {deletingAll ? (
                                <>
                                    <Loader className="animate-spin" size={16} />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <XCircle size={16} />
                                    Delete All Messages
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Warning Modal */}
            <Modal
                isOpen={showGenerateWarning}
                onClose={() => setShowGenerateWarning(false)}
                title="Confirm Message Suite Generation"
                size="md"
            >
                <div className="space-y-4">
                    {messageEstimate && messageEstimate.estimatedMessages > 0 ? (
                        <>
                            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-md">
                                <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={24} />
                                <div>
                                    <p className="font-semibold text-amber-900 mb-2">
                                        You are about to create {messageEstimate.estimatedMessages.toLocaleString()} messages
                                    </p>
                                    <p className="text-sm text-amber-800">
                                        This operation will create message templates in VoiceBox for:
                                    </p>
                                    <ul className="text-sm text-amber-800 mt-2 list-disc list-inside space-y-1">
                                        <li>{messageEstimate.templateCount} template(s)</li>
                                        <li>{messageEstimate.locationCount} location(s)</li>
                                        {messageEstimate.brandCount > 0 && (
                                            <li>{messageEstimate.brandCount} brand(s)</li>
                                        )}
                                    </ul>
                                    {messageEstimate.estimatedMessages > 100 && (
                                        <p className="text-sm font-medium text-amber-900 mt-3">
                                            ⚠️ This is a large operation and may take several minutes to complete.
                                        </p>
                                    )}
                                </div>
                            </div>
                            <p className="text-sm text-gray-600">
                                This will generate a preview of what would be created. No changes will be made to VoiceBox until you explicitly push the changes.
                            </p>
                        </>
                    ) : (
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                            <p className="text-sm text-yellow-800">
                                Unable to estimate message count. This operation may create a large number of messages.
                            </p>
                            <p className="text-sm text-yellow-800 mt-2">
                                Are you sure you want to proceed?
                            </p>
                        </div>
                    )}
                    <div className="flex justify-end gap-2 pt-4">
                        <button
                            onClick={() => setShowGenerateWarning(false)}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleGenerateConfirm}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Yes, Generate Suite
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Push to VoiceBox Warning Modal */}
            <Modal
                isOpen={showPushWarning}
                onClose={() => setShowPushWarning(false)}
                title="⚠️ WARNING: Update VoiceBox Database"
                size="lg"
            >
                <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-red-50 border-2 border-red-300 rounded-md">
                        <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={24} />
                        <div>
                            <p className="font-bold text-red-900 mb-2 text-lg">
                                You are about to UPDATE the VoiceBox database
                            </p>
                            <p className="text-sm text-red-800 mb-3">
                                This operation will make the following changes to VoiceBox:
                            </p>
                            {previewData && (
                                <ul className="text-sm text-red-800 list-disc list-inside space-y-1 mb-3">
                                    <li>Create {previewData.foldersToCreate?.length || 0} new folder(s)</li>
                                    <li>Create {previewData.messagesToCreate?.length || 0} new message(s)</li>
                                    <li>Update {previewData.messagesToUpdate?.length || 0} existing message(s)</li>
                                    {previewData.messagesToSkip?.length > 0 && (
                                        <li>Skip {previewData.messagesToSkip?.length} message(s) (already exist)</li>
                                    )}
                                </ul>
                            )}
                            <div className="bg-red-100 border border-red-300 rounded p-3 mt-3">
                                <p className="text-sm font-semibold text-red-900 mb-1">
                                    ⚠️ IMPORTANT WARNINGS:
                                </p>
                                <ul className="text-xs text-red-800 list-disc list-inside space-y-1">
                                    <li>This will modify the VoiceBox database directly</li>
                                    <li>Changes cannot be easily undone</li>
                                    <li>Existing messages will be updated if overwrite is enabled</li>
                                    <li>Make sure you have reviewed the preview above</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    
                    {selectedMapping && (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                            <p className="text-sm font-medium text-gray-900 mb-1">Target VoiceBox Database:</p>
                            <p className="text-sm text-gray-700">Client: {selectedMapping.voicebox_client_name}</p>
                            <p className="text-sm text-gray-700">Database: {selectedMapping.voicebox_database_name}</p>
                        </div>
                    )}

                    <p className="text-sm text-gray-700">
                        Type <strong>"PUSH"</strong> in the field below to confirm you understand this will update VoiceBox:
                    </p>
                    
                    <input
                        type="text"
                        id="push-confirmation"
                        placeholder="Type PUSH to confirm"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        onChange={(e) => {
                            const confirmInput = document.getElementById('push-confirmation');
                            const confirmButton = document.getElementById('confirm-push-button');
                            if (confirmButton) {
                                confirmButton.disabled = e.target.value !== 'PUSH';
                            }
                        }}
                    />

                    <div className="flex justify-end gap-2 pt-4 border-t">
                        <button
                            onClick={() => setShowPushWarning(false)}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            id="confirm-push-button"
                            onClick={handlePushConfirm}
                            disabled={true}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold"
                        >
                            Confirm Push to VoiceBox
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AdminMessageSuitePage;
